/* Daily digest banner: collapsible AI summary with citations, goals, and feed breakdown. */

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  Sparkle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertCircle,
} from "lucide-react";
import { useAISummary } from "@/hooks/useAISummary";
import {
  SUMMARY_STYLE_OPTIONS,
  formatSummaryDate,
} from "@/components/ai-summary/summaryStyleOptions";
import { SummaryPointsList } from "@/components/ai-summary/SummaryPointsList";
import { GoalSuggestionsSection } from "@/components/ai-summary/GoalSuggestionsSection";
import { FeedBreakdownSection } from "@/components/ai-summary/FeedBreakdownSection";

interface AISummaryBannerProps {
  feedItemCount: number;
  /** IDs of items currently in today's plan (greyed out in goal suggestions). */
  planItemIds?: Set<string>;
  /** Callback to add selected items to today's plan + queue. */
  onAddToPlan?: (ids: string[]) => void;
  /** Callback when a tag is clicked in the breakdown section. */
  onTagClick?: (tag: string) => void;
}

export function AISummaryBanner({ feedItemCount, planItemIds, onAddToPlan, onTagClick }: AISummaryBannerProps) {
  const {
    summary,
    isLoading,
    error,
    summaryStyle,
    isCollapsed,
    excludeReels,
    hoveredSegmentKey,
    breakdownData,
    setSummaryStyle,
    toggleCollapsed,
    toggleExcludeReels,
    generateSummary,
    setHoveredSegmentKey,
  } = useAISummary(feedItemCount);

  return (
    <div className="mx-4 md:mx-6 mb-2 rounded-lg border border-border bg-card overflow-hidden">
      <BannerHeader
        summary={summary}
        isCollapsed={isCollapsed}
        isLoading={isLoading}
        summaryStyle={summaryStyle}
        feedItemCount={feedItemCount}
        onToggleCollapsed={toggleCollapsed}
        onSetStyle={setSummaryStyle}
        onRefresh={generateSummary}
      />

      {!isCollapsed && (
        <BannerBody
          summary={summary}
          isLoading={isLoading}
          error={error}
          hoveredSegmentKey={hoveredSegmentKey}
          breakdownData={breakdownData}
          excludeReels={excludeReels}
          planItemIds={planItemIds}
          onAddToPlan={onAddToPlan}
          onHoverSegment={setHoveredSegmentKey}
          onToggleExcludeReels={toggleExcludeReels}
          onGenerate={generateSummary}
          onTagClick={onTagClick}
        />
      )}
    </div>
  );
}

/* ─── Header ─── */

interface BannerHeaderProps {
  summary: ReturnType<typeof useAISummary>["summary"];
  isCollapsed: boolean;
  isLoading: boolean;
  summaryStyle: string;
  feedItemCount: number;
  onToggleCollapsed: () => void;
  onSetStyle: (style: "concise" | "regular" | "comprehensive") => void;
  onRefresh: () => void;
}

function BannerHeader({
  summary,
  isCollapsed,
  isLoading,
  summaryStyle,
  feedItemCount,
  onToggleCollapsed,
  onSetStyle,
  onRefresh,
}: BannerHeaderProps) {
  const todayLocal = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const dateLabel = summary
    ? formatSummaryDate(summary.date)
    : formatSummaryDate(todayLocal);

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none transition-colors hover:bg-accent/50"
      onClick={onToggleCollapsed}
    >
      <Sparkles className="size-4 shrink-0 text-primary" />
      <span className="text-sm font-semibold">Daily Digest</span>
      <span className="text-xs text-muted-foreground hidden sm:inline">{dateLabel}</span>

      {/* Collapsed inline stats */}
      {isCollapsed && summary && (
        <span className="text-xs text-muted-foreground ml-auto mr-1 hidden sm:inline">
          {summary.totalItems} items &middot; {summary.readingTime}
        </span>
      )}

      {/* Expanded controls */}
      {!isCollapsed && (
        <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Style selector */}
          <div className="hidden sm:flex items-center gap-0.5 mr-1">
            {SUMMARY_STYLE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={summaryStyle === opt.value ? "outline" : "ghost"}
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => onSetStyle(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>

          <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 hidden sm:inline-flex">
            {feedItemCount} items
          </Badge>

          {summary && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={onRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`size-3.5 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>
      )}

      <Button
        variant="ghost"
        size="icon"
        className={`size-7 shrink-0 ${isCollapsed ? "" : "hidden sm:inline-flex"}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleCollapsed();
        }}
      >
        {isCollapsed ? <ChevronDown className="size-3.5" /> : <ChevronUp className="size-3.5" />}
      </Button>
    </div>
  );
}

/* ─── Body ─── */

interface BannerBodyProps {
  summary: ReturnType<typeof useAISummary>["summary"];
  isLoading: boolean;
  error: string | null;
  hoveredSegmentKey: string | null;
  breakdownData: ReturnType<typeof useAISummary>["breakdownData"];
  excludeReels: boolean;
  planItemIds?: Set<string>;
  onAddToPlan?: (ids: string[]) => void;
  onHoverSegment: (key: string | null) => void;
  onToggleExcludeReels: () => void;
  onGenerate?: () => void;
  onTagClick?: (tag: string) => void;
}

function BannerBody({
  summary,
  isLoading,
  error,
  hoveredSegmentKey,
  breakdownData,
  excludeReels,
  planItemIds,
  onAddToPlan,
  onHoverSegment,
  onToggleExcludeReels,
  onGenerate,
  onTagClick,
}: BannerBodyProps) {
  return (
    <div className="border-t border-border px-3 pb-3 pt-2 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Sparkle className="size-3.5 text-primary" style={{ animation: "sparklePulse 1.4s ease-in-out infinite" }} />
            <Sparkle className="size-3.5 text-primary" style={{ animation: "sparklePulse 1.4s ease-in-out 0.3s infinite" }} />
            <Sparkle className="size-3.5 text-primary" style={{ animation: "sparklePulse 1.4s ease-in-out 0.6s infinite" }} />
          </div>
          Generating your daily digest...
          <style>{`@keyframes sparklePulse { 0%, 100% { opacity: 0.2; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }`}</style>
        </div>
      )}

      {/* Empty state: no summary yet — show Generate button */}
      {!isLoading && !summary && onGenerate && (
        <div className="flex flex-col items-center gap-2 py-4">
          <p className="text-sm text-muted-foreground">No digest generated yet for today.</p>
          <Button size="sm" onClick={onGenerate} className="gap-1.5">
            <Sparkles className="size-3.5" />
            Generate Daily Digest
          </Button>
        </div>
      )}

      {/* Summary content */}
      {!isLoading && summary?.summaryPoints && summary.summaryPoints.length > 0 && (
        <SummaryPointsList
          points={summary.summaryPoints}
          hoveredSegmentKey={hoveredSegmentKey}
          onHoverSegment={onHoverSegment}
        />
      )}

      {/* Fallback plain summary */}
      {!isLoading && summary && !summary.summaryPoints && summary.summary && (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {summary.summary}
        </p>
      )}

      {/* Goal suggestions */}
      {!isLoading && summary?.goalSuggestions && summary.goalSuggestions.length > 0 && (
        <GoalSuggestionsSection goals={summary.goalSuggestions} planItemIds={planItemIds} onAddToPlan={onAddToPlan} />
      )}

      {/* Feed breakdown */}
      {breakdownData && (
        <FeedBreakdownSection
          data={breakdownData}
          excludeReels={excludeReels}
          onToggleExcludeReels={onToggleExcludeReels}
          onTagClick={onTagClick}
        />
      )}

      {/* Footer stats */}
      {!isLoading && summary && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t border-border">
          <span className="flex items-center gap-1">
            <Clock className="size-3" />
            {summary.readingTime} total
          </span>
          <span>{summary.totalItems} items analyzed</span>
        </div>
      )}
    </div>
  );
}
